import {
  useCallback,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { localStorageBridge, useAsyncEffect } from '../utils';

export type LocalStorageStateOptions<T> = {
  /** 저장 직렬화. 기본 `JSON.stringify`. 네이티브가 쓴 raw 값과 상호운용하려면 커스텀 지정. */
  serialize?: (value: T) => string;
  /** 복원 역직렬화. 기본 `JSON.parse`. throw 하면 저장값을 버리고 초기값을 유지. */
  deserialize?: (raw: string) => T;
};

const resolveInitial = <T>(initial: T | (() => T)): T =>
  initial instanceof Function ? (initial as () => T)() : initial;

const isUpdater = <T>(action: SetStateAction<T>): action is (prev: T) => T =>
  action instanceof Function;

/**
 * `localStorageBridge`(네이티브 로컬 저장소)에 동기화되는 `useState`.
 *
 * - 마운트 시 저장된 값을 비동기로 읽어 초기값을 대체(hydrate)한다.
 * - `setValue` 호출 시 자동으로 저장한다. 함수형 업데이트(`prev => next`)도 지원.
 * - 기본 JSON 직렬화라 객체/배열/원시값 모두 저장 가능하다.
 *
 * 저장 IO가 비동기라 hydrate 완료 전 첫 프레임은 `initialValue`가 그려지고,
 * 완료 후 저장값으로 교체된다. 교체 완료 여부는 반환 튜플의 3번째(`hydrated`)로 판단한다.
 * hydrate 로 인한 교체는 저장을 다시 트리거하지 않는다(에코 쓰기 방지).
 *
 * @param key localStorageBridge 키
 * @param initialValue 초기값. `useState`처럼 지연 초기화 함수도 허용.
 * @returns `[value, setValue, hydrated]`
 */
export function useLocalStorageState<T>(
  key: string,
  initialValue: T | (() => T),
  options?: LocalStorageStateOptions<T>,
): readonly [T, Dispatch<SetStateAction<T>>, boolean] {
  const [value, setInnerValue] = useState<T>(() => resolveInitial(initialValue));
  const [hydrated, setHydrated] = useState(false);

  // 최신값 참조 — 함수형 업데이트 해석과 저장 시 stale 클로저 방지.
  const valueRef = useRef(value);
  valueRef.current = value;

  // serialize/deserialize 는 매 렌더 새 함수일 수 있으므로 ref 로 고정 →
  // hydrate effect 가 오직 key 변경에만 반응하도록 한다.
  const serialize = useRef(options?.serialize);
  serialize.current = options?.serialize;
  const deserialize = useRef(options?.deserialize);
  deserialize.current = options?.deserialize;

  // key 가 바뀌면 다시 hydrate. 저장값이 없거나 역직렬화 실패면 현재값을 유지.
  useAsyncEffect(async () => {
    let cancelled = false;
    setHydrated(false);

    const raw = await localStorageBridge.getString(key);
    if (cancelled) return;

    if (raw != null) {
      try {
        const restored = (deserialize.current ?? JSON.parse)(raw) as T;
        valueRef.current = restored;
        setInnerValue(restored);
      } catch {
        // 역직렬화 실패 시 초기값 유지.
      }
    }
    setHydrated(true);

    return () => {
      cancelled = true;
    };
  }, [key]);

  const setValue = useCallback<Dispatch<SetStateAction<T>>>(
    action => {
      const next = isUpdater(action) ? action(valueRef.current) : action;
      valueRef.current = next;
      setInnerValue(next);
      const raw = (serialize.current ?? JSON.stringify)(next);
      localStorageBridge.putString(key, raw);
    },
    [key],
  );

  return [value, setValue, hydrated] as const;
}
